import React, { useState, useEffect } from 'react';
import { melanges } from '../data/melanges';
import ImageLoader from '../components/ImageLoader';
import '../styles/global.css';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

// Couleurs par mélange
const MELANGE_COLORS = {
  'rouge-obsidienne': {
    couleur: '#9C0101',
    nom: 'Rouge Obsidienne',
    nomCourt: 'R. Obsidienne',
    borderColor: '#9C0101' // Rouge
  },
  'bois-fauve': {
    couleur: '#1A3713',
    nom: 'Bois Fauve',
    nomCourt: 'Bois Fauve',
    borderColor: '#1A3713' // Vert
  },
  'eveil-floral': {
    couleur: '#728CC0',
    nom: 'Éveil Floral',
    nomCourt: 'É. Floral',
    borderColor: '#728CC0' // Bleu
  }
};

// Fonction pour extraire toutes les recettes de tous les mélanges
const getAllRecettes = () => {
  const allRecettes = [];
  
  melanges.forEach(melange => {
    if (melange.recettes && Array.isArray(melange.recettes)) {
      melange.recettes.forEach(recette => {
        allRecettes.push({
          ...recette,
          melangeId: melange.id,
          melangeNom: melange.nom,
          melangeColor: MELANGE_COLORS[melange.id]?.borderColor || '#111'
        });
      });
    }
  });
  
  return allRecettes;
};

export default function IdeesRecettes() {
  const navigate = useNavigate();
  const [selectedRecette, setSelectedRecette] = useState(null);
  const [filterMelange, setFilterMelange] = useState(null); // null = tous les mélanges
  const allRecettes = getAllRecettes();
  
  // Filtrer les recettes selon le mélange sélectionné
  const filteredRecettes = filterMelange 
    ? allRecettes.filter(r => r.melangeId === filterMelange)
    : allRecettes;

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isSmallMobile = windowWidth <= 480;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Détermine le nombre de colonnes selon la largeur
  const getGridColumns = () => {
    if (isSmallMobile) return 'repeat(2, 1fr)'; // 2 colonnes sur très petit écran
    if (isMobile) return 'repeat(2, 1fr)'; // 2 colonnes sur mobile
    if (isTablet) return 'repeat(3, 1fr)'; // 3 colonnes sur tablette
    return 'repeat(auto-fill, minmax(280px, 1fr))'; // Auto sur desktop
  };

  return (
    <>
      <Helmet>
        <title>Toutes les Recettes | KAZA</title>
        <meta name="description" content="Découvrez toutes nos recettes avec les mélanges d'épices KAZA : Rouge Obsidienne, Éveil Floral, Bois Fauve. Inspirez-vous pour sublimer vos plats !" />
        <meta property="og:title" content="Toutes les Recettes | KAZA" />
        <meta property="og:description" content="Découvrez toutes nos recettes avec les mélanges d'épices KAZA : Rouge Obsidienne, Éveil Floral, Bois Fauve. Inspirez-vous pour sublimer vos plats !" />
        <meta property="og:image" content="/images/logo-kaza-texte.svg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kaza-epices.com/idees-recettes" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Toutes les Recettes | KAZA" />
        <meta name="twitter:description" content="Découvrez toutes nos recettes avec les mélanges d'épices KAZA : Rouge Obsidienne, Éveil Floral, Bois Fauve. Inspirez-vous pour sublimer vos plats !" />
        <meta name="twitter:image" content="/images/logo-kaza-texte.svg" />
      </Helmet>

      <div className="container" style={{ maxWidth: 1400, margin: '0 auto', padding: isMobile ? '1rem 0.75rem' : '2rem 1rem' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
        <h1 style={{
          fontWeight: 700,
            fontSize: isSmallMobile ? '1.5rem' : isMobile ? '2rem' : '3rem',
            marginBottom: '1rem',
            color: '#111'
          }}>
            Toutes les Recettes
          </h1>
          <p style={{
            fontSize: isMobile ? '0.95rem' : '1.1rem',
            maxWidth: '700px',
            margin: '0 auto',
            color: '#666',
            padding: isMobile ? '0 0.5rem' : '0'
          }}>
            Découvrez toutes nos recettes pour sublimer vos plats avec nos mélanges d'épices KAZA
          </p>
        </div>

        {/* Filtres par mélange */}
        <div style={{
            display: 'flex',
            justifyContent: 'center',
          gap: isMobile ? '0.5rem' : '1rem',
          marginBottom: isMobile ? '2rem' : '3rem',
          flexWrap: 'wrap',
          padding: isMobile ? '0 0.5rem' : '0'
        }}>
          <button
            onClick={() => setFilterMelange(null)}
            style={{
              padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
              borderRadius: '25px',
              border: filterMelange === null ? '2px solid #111' : '2px solid #ddd',
              background: filterMelange === null ? '#111' : '#fff',
              color: filterMelange === null ? '#fff' : '#111',
              cursor: 'pointer',
              fontSize: isMobile ? '0.8rem' : '0.95rem',
              fontWeight: 600,
              transition: 'all 0.3s ease'
            }}
          >
            Toutes
          </button>
          {Object.entries(MELANGE_COLORS).map(([id, config]) => (
            <button
              key={id}
              onClick={() => setFilterMelange(id)}
              style={{
                padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                borderRadius: '25px',
                border: `2px solid ${config.borderColor}`,
                background: filterMelange === id ? config.borderColor : '#fff',
                color: filterMelange === id ? '#fff' : config.borderColor,
                cursor: 'pointer',
                fontSize: isMobile ? '0.8rem' : '0.95rem',
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }}
            >
              {config.nom}
            </button>
          ))}
        </div>

        {/* Grille de recettes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: getGridColumns(),
          gap: isMobile ? '0.75rem' : '2rem',
          marginBottom: '3rem',
          padding: isMobile ? '0 0.5rem' : '0'
        }}>
          {filteredRecettes.map((recette, index) => {
            const melangeConfig = MELANGE_COLORS[recette.melangeId];
            const borderColor = melangeConfig?.borderColor || '#ddd';
            
            return (
              <div
                key={index}
                className="card"
                style={{
                  background: '#fff',
                  borderRadius: isMobile ? '12px' : '20px',
                  overflow: 'hidden',
                  border: `${isMobile ? '2px' : '3px'} solid ${borderColor}`,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = `0 12px 32px ${borderColor}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                }}
                onClick={() => setSelectedRecette(recette)}
              >
                {/* Image de la recette */}
                <div style={{
                  width: '100%',
                  height: isMobile ? '140px' : '220px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <ImageLoader
                    src={recette.image || '/images/placeholder-recette.jpg'}
                    alt={`Recette : ${recette.titre} - KAZA`}
                    fallback={
                      <div style={{
                        height: '100%',
                        background: `linear-gradient(135deg, ${borderColor}22, ${borderColor}44)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: borderColor,
                        fontSize: isMobile ? '2rem' : '3rem'
                      }}>
                        {recette.titre.toLowerCase().includes('agneau') ? '🐑' :
                         recette.titre.toLowerCase().includes('poisson') ? '🐟' :
                         recette.titre.toLowerCase().includes('pleurote') || recette.titre.toLowerCase().includes('champignon') ? '🍄' :
                         recette.titre.toLowerCase().includes('poulet') ? '🍗' :
                         recette.titre.toLowerCase().includes('burger') ? '🍔' :
                         recette.titre.toLowerCase().includes('ceviche') ? '🥭' :
                         recette.titre.toLowerCase().includes('crème') || recette.titre.toLowerCase().includes('dessert') ? '🍮' :
                         recette.titre.toLowerCase().includes('tarte') ? '🥧' :
                         '👨‍🍳'}
                      </div>
                    }
                  style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {/* Tag du mélange */}
                  <div style={{
                    position: 'absolute',
                    top: isMobile ? '4px' : '12px',
                    right: isMobile ? '4px' : '12px',
                    background: borderColor,
                    color: '#fff',
                    padding: isMobile ? '0.15rem 0.4rem' : '0.4rem 1rem',
                    borderRadius: isMobile ? '10px' : '20px',
                    fontSize: isMobile ? '0.45rem' : '0.85rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: isMobile ? '0.02em' : '0.05em',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    maxWidth: isMobile ? '50%' : 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2
                  }}>
                    {isMobile 
                      ? (melangeConfig?.nomCourt || melangeConfig?.nom || recette.melangeNom)
                      : (melangeConfig?.nom || recette.melangeNom)}
                  </div>
                </div>

                {/* Contenu de la carte */}
                <div style={{
                  padding: isMobile ? '0.6rem' : '1.5rem',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <h3 style={{
                    fontSize: isMobile ? '0.8rem' : '1.3rem',
                    fontWeight: 700,
                    marginBottom: isMobile ? '0.4rem' : '0.75rem',
                    color: '#111',
                    lineHeight: 1.3
                  }}>
                    {recette.titre}
                  </h3>
                  
                  <p style={{
                    fontSize: isMobile ? '0.7rem' : '0.95rem',
                    color: '#666',
                    lineHeight: 1.4,
                    marginBottom: isMobile ? '0.6rem' : '1rem',
                    flex: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: isMobile ? 2 : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {recette.description}
                  </p>

                  <button
                    style={{
                      marginTop: 'auto',
                      padding: isMobile ? '0.4rem 0.8rem' : '0.75rem 1.5rem',
                      borderRadius: '25px',
                      border: `2px solid ${borderColor}`,
                      background: 'transparent',
                      color: borderColor,
                      cursor: 'pointer',
                      fontSize: isMobile ? '0.7rem' : '0.95rem',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = borderColor;
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = borderColor;
                    }}
                  >
                    Voir la recette
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Message si aucune recette */}
        {filteredRecettes.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#666'
          }}>
            <p style={{ fontSize: '1.1rem' }}>Aucune recette trouvée.</p>
          </div>
        )}
                        </div>

      {/* Modal de détails de la recette */}
      {selectedRecette && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          overflow: 'auto'
        }}
        onClick={() => setSelectedRecette(null)}
        >
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            border: `3px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer */}
            <button
              onClick={() => setSelectedRecette(null)}
              style={{
                                  position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#f5f5f5',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#111',
                width: '40px',
                height: '40px',
                                  borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#ddd';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
              }}
            >
              ✕
            </button>

            {/* Tag du mélange */}
            <div style={{
              display: 'inline-block',
              background: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd',
              color: '#fff',
              padding: '0.4rem 1rem',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '1rem'
            }}>
              {MELANGE_COLORS[selectedRecette.melangeId]?.nom || selectedRecette.melangeNom}
            </div>

            {/* Titre */}
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 700,
              marginBottom: '1rem',
              color: '#111',
              paddingRight: '3rem'
            }}>
              {selectedRecette.titre}
            </h2>

            {/* Image */}
            <div style={{
              width: '100%',
              height: '300px',
              borderRadius: '16px',
              marginBottom: '1.5rem',
              overflow: 'hidden'
            }}>
              <ImageLoader
                src={selectedRecette.image || '/images/placeholder-recette.jpg'}
                alt={`Recette : ${selectedRecette.titre}`}
                fallback={
                  <div style={{
                    height: '100%',
                    background: `linear-gradient(135deg, ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}22, ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}44)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '4rem'
                  }}>
                    👨‍🍳
                  </div>
                }
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>

            {/* Description */}
            <p style={{
              fontSize: '1.1rem',
              lineHeight: '1.7',
              marginBottom: '2rem',
              color: '#333'
            }}>
              {selectedRecette.description}
            </p>

            {/* Détails de la recette */}
            {selectedRecette.details && (
              <div>
                {/* Ingrédients */}
                {selectedRecette.details.ingredients && (
                  <div style={{
                    background: '#f9f9f9',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: `2px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`
                  }}>
                                <h4 style={{
                      fontSize: '1.2rem',
                                  fontWeight: 700,
                      marginBottom: '1rem',
                      color: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#111'
                    }}>
                      🛒 Ingrédients
                    </h4>
                    <ul style={{
                      fontSize: '1rem',
                      lineHeight: '1.8',
                      color: '#333',
                      paddingLeft: '1.5rem',
                                    margin: 0
                    }}>
                      {selectedRecette.details.ingredients.map((ingredient, index) => (
                        <li key={index} style={{ marginBottom: '0.5rem' }}>{ingredient}</li>
                      ))}
                        </ul>
                      </div>
                )}

                {/* Préparation */}
                {selectedRecette.details.preparation && (
                  <div style={{
                    background: '#f9f9f9',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: `2px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`
                  }}>
                    <h4 style={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      marginBottom: '1rem',
                      color: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#111'
                    }}>
                      👨‍🍳 Préparation
                    </h4>
                    <ol style={{
                      fontSize: '1rem',
                      lineHeight: '1.8',
                      color: '#333',
                      paddingLeft: '1.5rem',
                      margin: 0
                    }}>
                      {selectedRecette.details.preparation.map((step, index) => (
                        <li key={index} style={{ marginBottom: '0.75rem' }}>{step}</li>
                      ))}
                    </ol>
                    </div>
                )}

                {/* Accompagnements */}
                {selectedRecette.details.accompagnements && (
                  <div style={{
                    background: '#f9f9f9',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: `2px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`
                  }}>
                            <h4 style={{
                      fontSize: '1.2rem',
                              fontWeight: 700,
                      marginBottom: '1rem',
                      color: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#111'
                    }}>
                      🍽️ Accompagnements suggérés
                    </h4>
                    <ul style={{
                      fontSize: '1rem',
                      lineHeight: '1.8',
                      color: '#333',
                      paddingLeft: '1.5rem',
                                margin: 0
                    }}>
                      {selectedRecette.details.accompagnements.map((accomp, index) => (
                        <li key={index} style={{ marginBottom: '0.5rem' }}>{accomp}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Boissons */}
                {selectedRecette.details.boissons && (
                  <div style={{
                    background: '#f9f9f9',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: `2px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`
                  }}>
                    <h4 style={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      marginBottom: '1rem',
                      color: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#111'
                    }}>
                      🍷 Accords boissons
                    </h4>
                    <ul style={{
                      fontSize: '1rem',
                      lineHeight: '1.8',
                      color: '#333',
                      paddingLeft: '1.5rem',
                      margin: 0
                    }}>
                      {selectedRecette.details.boissons.map((boisson, index) => (
                        <li key={index} style={{ marginBottom: '0.5rem' }}>{boisson}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Autres détails (pourquoi, bonusChef, variante, astuce, dressage) */}
                {Object.entries(selectedRecette.details).map(([key, value]) => {
                  if (['ingredients', 'preparation', 'accompagnements', 'boissons'].includes(key)) return null;
                  if (!value) return null;
                  
                  const titles = {
                    pourquoi: '💡 Pourquoi ce mélange ?',
                    bonusChef: '👨‍🍳 Bonus du chef',
                    variante: '⚡ Variante express',
                    astuce: '💡 Astuce KAZA',
                    dressage: '🍽️ Dressage conseillé'
                  };
                  
                  return (
                    <div key={key} style={{
                      background: '#f9f9f9',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      marginBottom: '1.5rem',
                      border: `2px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`
                    }}>
                      <h4 style={{
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        marginBottom: '1rem',
                        color: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#111'
                      }}>
                        {titles[key] || key.charAt(0).toUpperCase() + key.slice(1)}
                      </h4>
                      <p style={{
                        fontSize: '1rem',
                        lineHeight: '1.7',
                        color: '#333',
                        margin: 0
                      }}>
                        {value}
                      </p>
              </div>
            );
          })}
              </div>
            )}

            {/* Bouton pour voir le mélange */}
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button
                onClick={() => {
                  setSelectedRecette(null);
                  navigate(`/melange/${selectedRecette.melangeId}`);
                }}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '25px',
                  border: `2px solid ${MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd'}`,
                  background: MELANGE_COLORS[selectedRecette.melangeId]?.borderColor || '#ddd',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Découvrir le mélange {MELANGE_COLORS[selectedRecette.melangeId]?.nom || selectedRecette.melangeNom}
              </button>
            </div>
        </div>
      </div>
      )}
    </>
  );
} 
